import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Trash2, X } from 'lucide-react';
import { ApiError, api } from '../lib/api';
import { Button, ConfirmDialog, Field, Input, Modal, Textarea } from '../components/ui';

export interface Medicacion {
  id: string;
  name: string;
  dose: string | null;
  times: string[];
  instructions: string | null;
}

/**
 * La otra medicación que la persona toma, cargada por ella misma.
 *
 * Es medicación concomitante autodeclarada, no una indicación. La app no la
 * prescribe, no sugiere dosis y no le dice a nadie que deje de tomar algo: lo
 * que hace es que esté anotada, que es lo que después importa cuando alguien
 * lee un evento adverso y necesita saber qué más estaba tomando.
 */
export function MedicacionModal({
  open,
  medicacion,
  onClose,
}: {
  open: boolean;
  medicacion: Medicacion | null;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();

  const [name, setName] = useState('');
  const [dose, setDose] = useState('');
  const [times, setTimes] = useState<string[]>([]);
  const [instructions, setInstructions] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [sacando, setSacando] = useState(false);

  useEffect(() => {
    if (!open) return;
    setError(null);
    setName(medicacion?.name ?? '');
    setDose(medicacion?.dose ?? '');
    setTimes(medicacion?.times ?? []);
    setInstructions(medicacion?.instructions ?? '');
  }, [open, medicacion]);

  const refrescar = (): void => {
    void queryClient.invalidateQueries({ queryKey: ['me', 'treatment'] });
    onClose();
  };

  const guardar = useMutation({
    mutationFn: () => {
      const cuerpo = {
        name: name.trim(),
        dose: dose.trim() || null,
        // Sin vacíos ni repetidos, y en orden: dos horarios iguales generarían
        // dos filas idénticas en la pantalla del día.
        times: [...new Set(times.filter(Boolean))].sort(),
        instructions: instructions.trim() || null,
      };
      return medicacion
        ? api.put(`/me/medications/${medicacion.id}`, cuerpo)
        : api.post('/me/medications', cuerpo);
    },
    onSuccess: refrescar,
    onError: (caught) =>
      setError(caught instanceof ApiError ? caught.message : 'No pudimos guardarla'),
  });

  const sacar = useMutation({
    mutationFn: () => api.delete(`/me/medications/${medicacion!.id}`),
    onSuccess: () => {
      setSacando(false);
      refrescar();
    },
  });

  return (
    <>
      <Modal
        open={open}
        onClose={onClose}
        title={medicacion ? 'Editar tu medicación' : 'Agregar una medicación'}
        footer={
          <div className="flex flex-wrap gap-2 justify-between w-full">
            {medicacion ? (
              <Button variant="ghost" onClick={() => setSacando(true)}>
                <Trash2 className="size-4" />
                Ya no la tomo
              </Button>
            ) : (
              <span />
            )}
            <div className="flex gap-2">
              <Button variant="ghost" onClick={onClose}>
                Cancelar
              </Button>
              <Button
                loading={guardar.isPending}
                onClick={() => {
                  if (!name.trim()) return setError('Poné el nombre');
                  setError(null);
                  guardar.mutate();
                }}
              >
                Guardar
              </Button>
            </div>
          </div>
        }
      >
        <div className="space-y-4">
          <p className="text-sm text-fg-muted">
            Anotá lo que tomás además de tu tratamiento. Sirve para que quede registrado y para que
            la app te lo recuerde.
          </p>

          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Nombre" required htmlFor="md-name">
              <Input
                id="md-name"
                value={name}
                autoFocus
                onChange={(event) => setName(event.target.value)}
              />
            </Field>

            <Field label="Dosis" htmlFor="md-dose" hint="Por ejemplo: 500 mg, media pastilla">
              <Input id="md-dose" value={dose} onChange={(event) => setDose(event.target.value)} />
            </Field>
          </div>

          <Field label="Horarios" htmlFor="md-time-0" hint="Sin ninguno, es a demanda">
            <div className="space-y-2">
              {times.map((time, index) => (
                <div key={index} className="flex gap-2">
                  <Input
                    id={`md-time-${index}`}
                    type="time"
                    value={time}
                    onChange={(event) =>
                      setTimes(times.map((valor, i) => (i === index ? event.target.value : valor)))
                    }
                  />
                  <Button
                    variant="ghost"
                    onClick={() => setTimes(times.filter((_, i) => i !== index))}
                    aria-label="Sacar este horario"
                  >
                    <X className="size-4" />
                  </Button>
                </div>
              ))}

              {times.length < 8 && (
                <Button variant="outline" size="sm" onClick={() => setTimes([...times, '08:00'])}>
                  <Plus className="size-4" />
                  Agregar un horario
                </Button>
              )}
            </div>
          </Field>

          <Field label="Indicaciones" htmlFor="md-notes">
            <Textarea
              id="md-notes"
              rows={2}
              value={instructions}
              placeholder="Con las comidas"
              onChange={(event) => setInstructions(event.target.value)}
            />
          </Field>

          {error && (
            <p className="text-sm text-danger" role="alert">
              {error}
            </p>
          )}
        </div>
      </Modal>

      {/*
        No se borra: se deja de tomar.
        Las tomas registradas son historia —dicen que durante dos meses tomó
        algo— y eso le importa a quien después lea sus eventos adversos.
      */}
      <ConfirmDialog
        open={sacando}
        title="Dejar de tomarla"
        description={`"${medicacion?.name ?? ''}" deja de aparecer en tu día. Lo que registraste hasta ahora se conserva.`}
        confirmLabel="Ya no la tomo"
        onCancel={() => setSacando(false)}
        onConfirm={() => sacar.mutate()}
      />
    </>
  );
}
